import { Component, lazy, Suspense, useEffect, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useRouteError,
} from "react-router-dom";

import { useBillingPlans, useOrganizations } from "@/api/hooks";
import { AdminGate } from "@/components/app/admin-gate";
import { FullPageSpinner } from "@/components/app/full-page-spinner";
import { NotFoundPage } from "@/components/app/not-found";
import { Button } from "@/components/ui/button";
import { BRAND_NAME } from "@/config/brand";
import { useAuth } from "@/lib/auth";
import { setNavigator } from "@/lib/notify";
import { lastOrg } from "@/lib/org";
import { screens } from "@/lib/routes";

// Route-level code splitting. The public pages (landing, sign-in) and the
// signed-in app are separate chunks, so neither downloads the other.
const authPages = () => import("@/features/auth/pages");
const LoginPage = lazy(() => authPages().then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => authPages().then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => authPages().then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => authPages().then((m) => ({ default: m.ResetPasswordPage })));
const ActivatePage = lazy(() => authPages().then((m) => ({ default: m.ActivatePage })));
const InvitePage = lazy(() => authPages().then((m) => ({ default: m.InvitePage })));
const NewOrganizationPage = lazy(() => authPages().then((m) => ({ default: m.NewOrganizationPage })));
const OrgLayout = lazy(() => import("@/components/app/org-layout").then((m) => ({ default: m.OrgLayout })));
const HomePage = lazy(() => screens.home().then((m) => ({ default: m.HomePage })));
const ChannelQueuePage = lazy(() => screens.channelQueue().then((m) => ({ default: m.ChannelQueuePage })));
const CalendarPage = lazy(() => screens.calendar().then((m) => ({ default: m.CalendarPage })));
const PostsPage = lazy(() => screens.posts().then((m) => ({ default: m.PostsPage })));
const ChannelsPage = lazy(() => screens.channels().then((m) => ({ default: m.ChannelsPage })));
const MediaPage = lazy(() => screens.media().then((m) => ({ default: m.MediaPage })));
const AnalyticsPage = lazy(() => screens.analytics().then((m) => ({ default: m.AnalyticsPage })));
const WebhooksPage = lazy(() => screens.webhooks().then((m) => ({ default: m.WebhooksPage })));
const FeedsPage = lazy(() => screens.feeds().then((m) => ({ default: m.FeedsPage })));
const AccountPage = lazy(() => screens.account().then((m) => ({ default: m.AccountPage })));
const PreviewPage = lazy(() =>
  import("@/features/public/preview-page").then((m) => ({ default: m.PreviewPage })),
);
const BillingSettings = lazy(() => screens.billing().then((m) => ({ default: m.BillingSettings })));
// The public landing page is its own chunk (with its images) and never loads the app screens.
const LandingPage = lazy(() =>
  import("@/features/marketing/landing-page").then((m) => ({ default: m.LandingPage })),
);
const PricingPage = lazy(() =>
  import("@/features/billing/pricing-page").then((m) => ({ default: m.PricingPage })),
);
const settings = screens.settings;
const SettingsLayout = lazy(() => settings().then((m) => ({ default: m.SettingsLayout })));
const GeneralSettings = lazy(() => settings().then((m) => ({ default: m.GeneralSettings })));
const MembersSettings = lazy(() => settings().then((m) => ({ default: m.MembersSettings })));
const ApiKeysSettings = lazy(() => settings().then((m) => ({ default: m.ApiKeysSettings })));
const CustomersSettings = lazy(() => settings().then((m) => ({ default: m.CustomersSettings })));
const TagsSettings = lazy(() => settings().then((m) => ({ default: m.TagsSettings })));
const SignaturesSettings = lazy(() => settings().then((m) => ({ default: m.SignaturesSettings })));
const TemplatesSettings = lazy(() => settings().then((m) => ({ default: m.TemplatesSettings })));

function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous")
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
}

/** A public page, loaded on demand. */
function Public({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/**
 * `/`: the landing page for signed-out visitors, the workspace for signed-in
 * users. Waits for the silent sign-in so signed-in users never see a flash of
 * the landing page.
 */
function RootRoute() {
  const { status } = useAuth();
  // Start loading the public plans during the silent sign-in, so the landing
  // page's trial wording is ready when it renders (cached for an hour).
  useBillingPlans();

  useEffect(() => {
    // index.html carries the landing page's title for crawlers; the app uses the brand name
    if (status === "authenticated") document.title = BRAND_NAME;
  }, [status]);

  if (status === "loading") return <FullPageSpinner />;
  if (status === "authenticated") return <HomeRedirect />;

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <LandingPage />
    </Suspense>
  );
}

/** Sends users to their last (or first) organization. */
function HomeRedirect({ to = "home" }: { to?: string }) {
  const orgs = useOrganizations();
  const location = useLocation();

  if (orgs.isLoading) return <FullPageSpinner />;

  const remembered = lastOrg();
  const target = orgs.data?.find((o) => o.id === remembered) ?? orgs.data?.[0];
  if (!target) return <Navigate to="/organizations/new" replace />;

  return <Navigate to={`/o/${target.id}/${to}${location.search}`} replace />;
}

function RouteError() {
  const error = useRouteError() as { status?: number; statusText?: string; message?: string };
  if (error?.status === 404) return <NotFoundPage />;

  return (
    <main className="flex h-dvh flex-col items-center justify-center gap-3 px-4 text-center">
      <h1 className="text-lg font-semibold tracking-tight">Something went wrong</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        An unexpected error occurred. Reloading usually helps.
      </p>
      <div className="mt-2 flex gap-2">
        <Button onClick={() => window.location.reload()}>Reload</Button>
        <Button variant="primary" onClick={() => window.location.assign("/")}>
          Go home
        </Button>
      </div>
    </main>
  );
}

const router = createBrowserRouter(
  [
    {
      errorElement: <RouteError />,
      children: [
        { path: "/", element: <RootRoute /> },
        {
          path: "/login",
          element: (
            <Public>
              <LoginPage />
            </Public>
          ),
        },
        {
          path: "/register",
          element: (
            <Public>
              <RegisterPage />
            </Public>
          ),
        },
        {
          path: "/forgot-password",
          element: (
            <Public>
              <ForgotPasswordPage />
            </Public>
          ),
        },
        {
          path: "/reset-password",
          element: (
            <Public>
              <ResetPasswordPage />
            </Public>
          ),
        },
        {
          path: "/activate",
          element: (
            <Public>
              <ActivatePage />
            </Public>
          ),
        },
        {
          path: "/invite",
          element: (
            <Public>
              <InvitePage />
            </Public>
          ),
        },
        {
          path: "/pricing",
          element: (
            <Suspense fallback={<FullPageSpinner />}>
              <PricingPage />
            </Suspense>
          ),
        },
        {
          path: "/p/:groupId",
          element: (
            <Suspense fallback={<FullPageSpinner />}>
              <PreviewPage />
            </Suspense>
          ),
        },
        // Platform admin console (backend/docs/admin.md), a separate chunk. Not
        // behind RequireAuth: signed-out visitors get the plain not-found page
        // instead of a sign-in redirect, so the route's existence isn't revealed.
        { path: "/admin/*", element: <AdminGate /> },
        {
          element: <RequireAuth />,
          children: [
            // links from notifications / emails
            { path: "/launches", element: <HomeRedirect to="calendar" /> },
            { path: "/account", element: <AccountPage /> },
            { path: "/organizations/new", element: <NewOrganizationPage /> },
            {
              path: "/o/:orgId",
              element: <OrgLayout />,
              children: [
                { index: true, element: <Navigate to="home" replace /> },
                { path: "home", element: <HomePage /> },
                { path: "calendar", element: <CalendarPage /> },
                { path: "posts", element: <PostsPage /> },
                { path: "channels", element: <ChannelsPage /> },
                { path: "channels/:channelId", element: <ChannelQueuePage /> },
                { path: "media", element: <MediaPage /> },
                { path: "analytics", element: <AnalyticsPage /> },
                { path: "webhooks", element: <WebhooksPage /> },
                { path: "feeds", element: <FeedsPage /> },
                {
                  path: "settings",
                  element: <SettingsLayout />,
                  children: [
                    { index: true, element: <Navigate to="general" replace /> },
                    { path: "general", element: <GeneralSettings /> },
                    { path: "members", element: <MembersSettings /> },
                    { path: "api-keys", element: <ApiKeysSettings /> },
                    { path: "customers", element: <CustomersSettings /> },
                    { path: "tags", element: <TagsSettings /> },
                    { path: "signatures", element: <SignaturesSettings /> },
                    { path: "templates", element: <TemplatesSettings /> },
                    { path: "billing", element: <BillingSettings /> },
                  ],
                },
              ],
            },
          ],
        },
        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

// Lets toasts (rendered outside the router) navigate without a full reload.
setNavigator((to) => void router.navigate(to));

/** Last-resort boundary for errors outside the router. */
export class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error(error);
  }

  render() {
    return this.state.failed ? (
      <p className="p-6 text-sm">{BRAND_NAME} failed to load. Please reload the page.</p>
    ) : (
      this.props.children
    );
  }
}

export function App() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
